import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  Activity, Truck, CheckCircle2, ChevronRight,
  Building2, Mail, Phone, Users, Layers,
} from "lucide-react";

const STATUS_COLOR = {
  "Orders In": "info", "Picking": "warning", "To Invoice": "warning",
  "Completed": "success",
};

export default function PortalOverviewPage() {
  const [searchParams] = useSearchParams();
  const asCompany = searchParams.get("as_company");
  const apiParams = asCompany ? { as_company: asCompany } : {};
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [err, setErr] = useState("");

  useEffect(() => {
    Promise.all([api.get("/portal/overview", { params: apiParams }), api.get("/inventory")])
      .then(([o, p]) => { setData(o.data); setProducts(p.data); })
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  if (err) return <div className="es-card p-4 text-sm text-red-700">{err}</div>;
  if (!data) return <div className="text-sm text-zinc-500">Loading portal…</div>;

  const productMap = Object.fromEntries(products.map((p) => [p.id, p]));
  const co = data.company || {};
  const contacts = data.contacts || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Welcome, {co.Company_Name}</h1>
        <p className="text-sm text-zinc-500 mt-1">{co.Account_Tier} tier · {co.Discount_Percent || 0}% standing discount</p>
      </div>

      {/* Company + Contacts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Company info */}
        <div className="es-card p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-brand-50 border border-brand-100 flex items-center justify-center flex-shrink-0">
              <Building2 className="w-6 h-6 text-brand-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-semibold text-lg text-zinc-900 truncate">{co.Company_Name}</div>
              <div className="flex items-center gap-1.5 mt-1">
                <span className="es-badge es-badge-info">{co.Account_Tier}</span>
                <span className={`es-badge ${co.Status === "Active" ? "es-badge-success" : "es-badge-neutral"}`}>{co.Status || "Active"}</span>
              </div>
            </div>
          </div>
          <div className="space-y-2 text-sm">
            <InfoRow icon={<Mail className="w-3.5 h-3.5" />} label="Email"
              value={co.Email ? <a href={`mailto:${co.Email}`} className="text-brand-700 hover:underline">{co.Email}</a> : "—"} />
            <InfoRow icon={<Phone className="w-3.5 h-3.5" />} label="Phone"
              value={co.Phone ? <a href={`tel:${co.Phone}`}>{co.Phone}</a> : "—"} />
          </div>
        </div>

        {/* Contacts panel */}
        <div className="es-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Users className="w-4 h-4 text-brand-600" />
            <h2 className="font-display font-semibold">Contacts ({contacts.length})</h2>
          </div>
          {contacts.length === 0 ? (
            <div className="text-sm text-zinc-400 text-center py-6">No contacts on file for your account.</div>
          ) : (
            <div className="space-y-3">
              {contacts.map((c) => (
                <div key={c.id} data-testid={`portal-contact-${c.id}`} className="flex items-start gap-3 pb-3 border-b border-zinc-100 last:border-0 last:pb-0">
                  <div className="w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-zinc-600">
                    {(c.First_Name?.[0] || "") + (c.Last_Name?.[0] || "")}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-sm text-zinc-900">{c.First_Name} {c.Last_Name}</div>
                    {c.Job_Title && <div className="text-xs text-zinc-500">{c.Job_Title}</div>}
                    <div className="flex flex-wrap items-center gap-3 mt-1 text-xs">
                      {c.Email && <a href={`mailto:${c.Email}`} className="text-brand-700 hover:underline inline-flex items-center gap-1"><Mail className="w-3 h-3" />{c.Email}</a>}
                      {c.Phone && <a href={`tel:${c.Phone}`} className="text-zinc-600 inline-flex items-center gap-1"><Phone className="w-3 h-3" />{c.Phone}</a>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile testid="portal-kpi-active"    label="Active Orders"      value={data.stats.active_orders} icon={Activity}     color="brand" />
        <Tile testid="portal-kpi-dockets"   label="Pending Dockets"    value={data.stats.pending_dockets} icon={Truck}      color="blue" />
        <Tile testid="portal-kpi-allocated" label="Allocated (pending)" value={data.stats.allocated_pending} icon={Layers}   color="amber"
          sublabel="units in production" />
        <Tile testid="portal-kpi-completed" label="Completed Orders"   value={data.stats.completed_orders} icon={CheckCircle2} color="green" />
      </div>

      {/* Active orders list */}
      <div className="es-card">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="font-display font-semibold">Active Orders</h2>
          <Link to="/portal/orders" className="text-xs text-brand-700 hover:underline flex items-center gap-1">
            View all <ChevronRight className="w-3 h-3" />
          </Link>
        </div>
        <table className="es-table">
          <thead><tr><th>Product</th><th className="text-right">Quantity</th><th>Status</th><th>Created</th></tr></thead>
          <tbody>
            {data.recent_orders.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-sm text-zinc-400">No orders yet.</td></tr>}
            {data.recent_orders.map((o) => (
              <tr key={o.id} data-testid={`portal-recent-${o.id}`}>
                <td className="font-semibold">{productMap[o.Product_ID]?.Product_Name || "—"}</td>
                <td className="text-right font-mono font-bold">{o.Quantity_Ordered}</td>
                <td><span className={`es-badge es-badge-${STATUS_COLOR[o.Order_Status] || "neutral"}`}>{o.Order_Status}</span></td>
                <td className="font-mono text-xs">{o.created_date?.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-400 flex-shrink-0">{icon}</span>
      <span className="text-[10px] uppercase tracking-wider text-zinc-500 w-32 flex-shrink-0">{label}</span>
      <span className="text-zinc-800 truncate">{value}</span>
    </div>
  );
}

function Tile({ label, value, sublabel, icon: Icon, color, testid }) {
  const colors = {
    brand: "bg-brand-50 text-brand-700",
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    green: "bg-green-50 text-green-700",
  };
  return (
    <div data-testid={testid} className="es-card p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colors[color]} flex-shrink-0`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="min-w-0">
        <div className="font-display text-2xl font-bold leading-tight">{value}</div>
        <div className="text-xs text-zinc-700 font-semibold leading-tight">{label}</div>
        {sublabel && <div className="text-[10px] text-zinc-500 leading-tight mt-0.5">{sublabel}</div>}
      </div>
    </div>
  );
}
